import axios from "axios";
import myConfig from "../../configs/config";

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("client-token")}`,
  };
}

export function withSelectionParams(farmId, selection = {}, extra = {}) {
  const safeSelection = selection || {};
  const params = { farm_id: farmId, ...extra };

  const operatorIds = safeSelection.operatorIds || [];
  if (operatorIds.length) {
    params.operator_ids = operatorIds.join(",");
  }

  const equipmentIds = safeSelection.equipmentIds || [];
  const machineIdents = safeSelection.machineIdents || [];
  // "Todas as máquinas" resolve no período (ASSUMIR_MAQUINA), sem lista vitalícia.
  if (safeSelection.allMachines) {
    params.all_machines = 1;
  } else {
    if (equipmentIds.length) {
      params.equipment_ids = equipmentIds.join(",");
    }
    if (machineIdents.length) {
      params.machine_idents = machineIdents.join(",");
    }
  }

  if (safeSelection.dateFrom) {
    params.date_from = safeSelection.dateFrom;
  }
  if (safeSelection.dateTo) {
    params.date_to = safeSelection.dateTo;
  }

  return params;
}

export async function fetchFieldAppFilters(farmId, operatorIds = []) {
  const params = { farm_id: farmId };
  if (operatorIds && operatorIds.length) {
    params.operator_ids = operatorIds.join(",");
  }
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/filters/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchObservation(observationId) {
  const response = await axios.get(
    `${myConfig.API_URL}/observation/${observationId}/`,
    {
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchFieldAppSummary(farmId, selection = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/summary/`,
    {
      params: withSelectionParams(farmId, selection),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchFuelingRecords(farmId, params = {}, selection = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/fueling-records/`,
    {
      params: withSelectionParams(farmId, selection, { limit: 500, ...params }),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchSyncRecords(farmId, params = {}, selection = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/sync-records/`,
    {
      params: withSelectionParams(farmId, selection, { limit: 100, ...params }),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchSyncRecordDetail(farmId, recordUuid) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/sync-records/${recordUuid}/`,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchChecklistReports(farmId) {
  const response = await axios.get(
    `${myConfig.API_URL}/checklist-report/?farm_id=${farmId}`,
    { headers: authHeaders() }
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function fetchScheduledMaintenances(
  farmId,
  params = {},
  selection = {}
) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/scheduled-maintenances/`,
    {
      params: withSelectionParams(farmId, selection, { limit: 100, ...params }),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function updateFieldMaintenanceRecord(
  farmId,
  recordUuid,
  payload
) {
  const response = await axios.patch(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/maintenance-records/${recordUuid}/`,
    payload,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function completeScheduledMaintenance(
  farmId,
  maintenanceId,
  payload
) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/scheduled-maintenances/${maintenanceId}/complete/`,
    payload,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function updateScheduledMaintenance(
  farmId,
  maintenanceId,
  payload
) {
  const response = await axios.patch(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/scheduled-maintenances/${maintenanceId}/`,
    payload,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function createScheduledMaintenance(farmId, payload) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/scheduled-maintenances/`,
    payload,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchFieldAppTracking(farmId, selection = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/tracking/`,
    {
      params: withSelectionParams(farmId, selection),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchFieldAppWeatherAt(
  farmId,
  { timestamp, operatorIds = [], lat, lng } = {}
) {
  const params = { farm_id: farmId, timestamp };
  if (operatorIds && operatorIds.length) {
    params.operator_ids = operatorIds.join(",");
  }
  if (lat != null && lat !== "") params.lat = lat;
  if (lng != null && lng !== "") params.lng = lng;
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/weather-at/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return (response.data && response.data.weather) || null;
}

export async function fetchFarmWeatherLog(farmId, { dateFrom, dateTo } = {}) {
  const params = { farm_id: farmId };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/farm-weather/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return (response.data && response.data.results) || [];
}

export async function fetchTelemetrySchedule(farmId, selection = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/telemetry-schedule/`,
    {
      params: withSelectionParams(farmId, selection),
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchMachineSchedule(
  farmId,
  { machineIdent, operatorIds = [], dateFrom, dateTo, landPlotId = null } = {}
) {
  const params = {
    farm_id: farmId,
    machine_ident: machineIdent,
    date_from: dateFrom,
    date_to: dateTo,
  };
  if (operatorIds.length) {
    params.operator_ids = operatorIds.join(",");
  }
  if (landPlotId) {
    params.land_plot_id = landPlotId;
  }
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/machine-schedule/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchMachinePeriodSummary(
  farmId,
  {
    operatorIds = [],
    machineIdent,
    dateFrom,
    dateTo,
  } = {}
) {
  const params = {
    farm_id: farmId,
    machine_ident: machineIdent,
    date_from: dateFrom,
    date_to: dateTo,
  };
  if (operatorIds.length) {
    params.operator_ids = operatorIds.join(",");
  }
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/machine-period-summary/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchRadioGroups(farmId) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/radio/groups/`,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchRadioMessages(farmId, params = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/radio/messages/`,
    {
      params: { farm_id: farmId, ...params },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchOperatorAlerts(farmId) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/operator-alerts/`,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function sendOperatorAlert(farmId, payload) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/operator-alerts/?farm_id=${farmId}`,
    { ...payload, farm_id: farmId },
    { headers: authHeaders() }
  );
  return response.data;
}

export function recalcTelemetryAreaHa(pathDistanceKm, workingWidthM) {
  if (pathDistanceKm == null || workingWidthM == null) return null;
  return (
    Math.round(((pathDistanceKm * 1000 * workingWidthM) / 10000) * 100) / 100
  );
}

export async function updateWorkingWidth(
  farmId,
  comparison,
  widthM,
  operationId = null
) {
  const width = String(widthM);
  const headers = authHeaders();

  if (comparison && comparison.equipmentId) {
    const formData = new FormData();
    formData.append("length", width);
    await axios.patch(
      `${myConfig.API_URL}/equipment/${comparison.equipmentId}/`,
      formData,
      { headers }
    );
    return recalcTelemetryAreaHa(comparison.pathDistanceKm, widthM);
  }

  const targetOperationId =
    (comparison && comparison.monitoringOperationId) || operationId;

  if (targetOperationId && farmId) {
    const formData = new FormData();
    formData.append("width", width);
    await axios.patch(
      `${myConfig.API_URL}/operation/${targetOperationId}/?farm_id=${farmId}`,
      formData,
      { headers }
    );
    return recalcTelemetryAreaHa(comparison.pathDistanceKm, widthM);
  }

  throw new Error("missing width target");
}

export async function updateSessionFinishReadings(
  farmId,
  {
    sessionUuid,
    hourMeter,
    odometer,
    managerNote,
    finishTimestamp,
    finishLatitude,
    finishLongitude,
  }
) {
  const response = await axios.patch(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/session-finish-readings/`,
    {
      sessionUuid,
      hourMeter,
      odometer,
      managerNote,
      finishTimestamp,
      finishLatitude,
      finishLongitude,
    },
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchActiveTelemetry(farmId) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/active-telemetry/`,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function fetchPendingActions() {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/pending-actions/`,
    {
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function createPointOfInterestServiceOrder(farmId, payload) {
  const response = await axios.post(
    `${myConfig.API_URL}/observation/`,
    {
      farm_id: parseInt(farmId, 10),
      service_type: "ponto_interesse",
      ...payload,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

export async function acknowledgePendingAction({
  recordUuid,
  actionType,
  managerNote,
  acceptWithoutData,
}) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/pending-actions/acknowledge/`,
    {
      recordUuid,
      actionType,
      managerNote,
      acceptWithoutData: acceptWithoutData || undefined,
    },
    {
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function correctPendingMeterReading({
  recordUuid,
  correctRecordUuid,
  targetRole,
  actionType,
  hourMeter,
  odometer,
  assumeHourMeter,
  assumeOdometer,
  managerNote,
}) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/field-data/pending-actions/correct-meter-reading/`,
    {
      recordUuid,
      correctRecordUuid,
      targetRole,
      actionType,
      hourMeter,
      odometer,
      assumeHourMeter,
      assumeOdometer,
      managerNote,
    },
    {
      headers: authHeaders(),
    }
  );
  return response.data;
}
